import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { t } from '../../i18n';

import s from './ProfileCardFilterList.module.scss';
import ProfileCardLink from '../ProfileCardLink/ProfileCardLink';
import Popover from '../Popover/Popover';
import Dropdown from '../Dropdown/Dropdown';
import Checkbox from '../Checkbox/Checkbox';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { useIndividualsFilters } from '../../containers/ConnectionsPage/hooks/useIndividualsFilters';
import IconText from '../IconText/IconText';
import AnimatedLogo from '../AnimatedLogo/AnimatedLogo';

const ProfileCardFilterList = ({
    individuals = [],
    disabled,
    selectedCompany,
    holdings = [],
    onClearCompany,
    isClearingCompany = false,
}) => {
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const [companySearch, setCompanySearch] = useState('');
    const [sectorSearch, setSectorSearch] = useState('');
    const popoverRef = useRef(null);

    const companyTypes = holdings.map((x) => {
        return {
            id: x.sourceId,
            value: x.name,
            label: x.name,
        };
    });

    const {
        filteredIndividuals,
        politicalTypes = [],
        sectorTypes = [],
        selectedPolitics = {},
        setSelectedPolitics,
        selectedSectors = {},
        setSelectedSectors,
        selectedCompanies = {},
        setSelectedCompanies,
        searchTerm = '',
        setSearchTerm,
        activeFilters,
    } = useIndividualsFilters(individuals, selectedCompany, companyTypes);

    const toggleFilterDropdown = () =>
        setIsFilterDropdownOpen(!isFilterDropdownOpen);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target)
            ) {
                setIsFilterDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const showCheckmark = (selectedMap) =>
        Object.values(selectedMap || {}).some(Boolean);

    const filteredCompanies = useMemo(() => {
        return companyTypes.filter((item) =>
            item.label.toLowerCase().includes(companySearch.toLowerCase())
        );
    }, [companyTypes, companySearch]);

    const filteredSectors = useMemo(() => {
        return sectorTypes.filter((item) =>
            item.label.toLowerCase().includes(sectorSearch.toLowerCase())
        );
    }, [sectorTypes, sectorSearch]);

    const isFiltering = useMemo(() => {
        return (
            Object.values(selectedPolitics || {}).some(Boolean) ||
            Object.values(selectedSectors || {}).some(Boolean) ||
            Object.values(selectedCompanies || {}).some(Boolean) ||
            (searchTerm || '').length > 0
        );
    }, [selectedPolitics, selectedSectors, selectedCompanies, searchTerm]);

    const isEmptyAndUnfiltered = !individuals.length && activeFilters === 0;

    return (
        <div className={s.Root}>
            <div className={s.Container}>
                <div
                    className={classNames(s.FilterWrapper, {
                        [s['FilterWrapper--Disabled']]: disabled,
                    })}>
                    <Popover
                        popoverRef={popoverRef}
                        isPopoverOpen={isFilterDropdownOpen}
                        onClick={toggleFilterDropdown}
                        minWidth="250px"
                        modifier={'FullWidth'}
                        overrideButton={
                            <div className={s.FilterButtonWrapper}>
                                <Button
                                    title={
                                        isFilterDropdownOpen
                                            ? 'Close'
                                            : 'Filter'
                                    }
                                    modifier="Filter"
                                    size="Small"
                                    icon={
                                        isEmptyAndUnfiltered
                                            ? 'Filter'
                                            : 'FilterWhite'
                                    }
                                    isActive={isFilterDropdownOpen}
                                    disabled={disabled || isEmptyAndUnfiltered}
                                    description={
                                        activeFilters > 0 &&
                                        `(${activeFilters})`
                                    }
                                />
                            </div>
                        }
                        content={
                            <div className={s.PopoverContent}>
                                {politicalTypes.length > 0 && (
                                    <Dropdown
                                        modifiers={['BorderBottom']}
                                        title={t(
                                            'mapFilter.politicalAffiliation'
                                        )}
                                        showCheckmark={showCheckmark(
                                            selectedPolitics
                                        )}>
                                        <ul className={s.CheckboxList}>
                                            {politicalTypes.map((item) => (
                                                <li
                                                    key={item.id}
                                                    className={s.CheckboxItem}>
                                                    <span
                                                        className={
                                                            s.CheckboxLabel
                                                        }>
                                                        {item.label}
                                                    </span>
                                                    <Checkbox
                                                        modifier="Alternative"
                                                        value={item.id}
                                                        checked={
                                                            selectedPolitics[
                                                                item.value
                                                            ] || false
                                                        }
                                                        onChange={() =>
                                                            setSelectedPolitics(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    [item.value]:
                                                                        !prev[
                                                                            item
                                                                                .value
                                                                        ],
                                                                })
                                                            )
                                                        }
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </Dropdown>
                                )}

                                {sectorTypes.length > 0 && (
                                    <Dropdown
                                        modifiers={['BorderBottom']}
                                        title={t('mapFilter.sector')}
                                        showCheckmark={showCheckmark(
                                            selectedSectors
                                        )}>
                                        <div
                                            className={s.DropdownSearchWrapper}>
                                            <Icon
                                                name="Search"
                                                className={s.DropdownSearchIcon}
                                                size="Small"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Search sectors"
                                                value={sectorSearch}
                                                onChange={(e) =>
                                                    setSectorSearch(
                                                        e.target.value
                                                    )
                                                }
                                                className={s.DropdownSearch}
                                            />
                                            {sectorSearch.length > 0 && (
                                                <button
                                                    className={s.DropdownClear}
                                                    onClick={() =>
                                                        setSectorSearch('')
                                                    }>
                                                    <Icon
                                                        name="Close"
                                                        className={
                                                            s.DropdownClearIcon
                                                        }
                                                        size="Small"
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        <ul className={s.CheckboxListScroll}>
                                            {filteredSectors.map((item) => (
                                                <li
                                                    key={item.id}
                                                    className={s.CheckboxItem}>
                                                    <span
                                                        className={
                                                            s.CheckboxLabel
                                                        }>
                                                        {item.label}
                                                    </span>
                                                    <Checkbox
                                                        modifier="Alternative"
                                                        value={item.id}
                                                        checked={
                                                            selectedSectors[
                                                                item.value
                                                            ] || false
                                                        }
                                                        onChange={() =>
                                                            setSelectedSectors(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    [item.value]:
                                                                        !prev[
                                                                            item
                                                                                .value
                                                                        ],
                                                                })
                                                            )
                                                        }
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </Dropdown>
                                )}

                                {companyTypes.length > 0 && (
                                    <Dropdown
                                        modifiers={['BorderBottom']}
                                        title={t('mapFilter.company')}
                                        showCheckmark={showCheckmark(
                                            selectedCompanies
                                        )}>
                                        <div
                                            className={s.DropdownSearchWrapper}>
                                            <Icon
                                                name="Search"
                                                className={s.DropdownSearchIcon}
                                                size="Small"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Search companies"
                                                value={companySearch}
                                                onChange={(e) =>
                                                    setCompanySearch(
                                                        e.target.value
                                                    )
                                                }
                                                className={s.DropdownSearch}
                                            />
                                            {companySearch.length > 0 && (
                                                <button
                                                    className={s.DropdownClear}
                                                    onClick={() =>
                                                        setCompanySearch('')
                                                    }>
                                                    <Icon
                                                        name="Close"
                                                        className={
                                                            s.DropdownClearIcon
                                                        }
                                                        size="Small"
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        <ul className={s.CheckboxListScroll}>
                                            {filteredCompanies.map((item) => (
                                                <li
                                                    key={item.id}
                                                    className={s.CheckboxItem}>
                                                    <span
                                                        className={
                                                            s.CheckboxLabel
                                                        }>
                                                        {item.label}
                                                    </span>
                                                    <Checkbox
                                                        modifier="Alternative"
                                                        value={item.id}
                                                        checked={
                                                            selectedCompanies[
                                                                item.value
                                                            ] || false
                                                        }
                                                        onChange={() =>
                                                            setSelectedCompanies(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    [item.value]:
                                                                        !prev[
                                                                            item
                                                                                .value
                                                                        ],
                                                                })
                                                            )
                                                        }
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </Dropdown>
                                )}

                                {(Object.values(selectedPolitics).some(
                                    Boolean
                                ) ||
                                    Object.values(selectedSectors).some(
                                        Boolean
                                    ) ||
                                    Object.values(selectedCompanies).some(
                                        Boolean
                                    ) ||
                                    searchTerm.length > 0) && (
                                    <div className={s.FilterButtons}>
                                        <button
                                            className={s.ClearAllFilters}
                                            onClick={() => {
                                                setSelectedPolitics({});
                                                setSelectedSectors({});
                                                setSelectedCompanies({});
                                                setSearchTerm('');
                                                setCompanySearch('');
                                                setSectorSearch('');
                                                setIsFilterDropdownOpen(false);
                                                if (selectedCompany) {
                                                    onClearCompany?.();
                                                }
                                            }}>
                                            {t('mapFilter.clear')}
                                            <Icon
                                                name="Close"
                                                className={s.FilterIcon}
                                            />
                                        </button>
                                        <div className={s.FilterSeparator} />
                                        <button
                                            className={s.ClearAllFilters}
                                            onClick={() =>
                                                setIsFilterDropdownOpen(false)
                                            }>
                                            {t('mapFilter.done')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        }
                    />
                    <div
                        className={classNames(s.Search, {
                            [s['Search--Disabled']]:
                                disabled || isEmptyAndUnfiltered,
                        })}>
                        <div
                            className={classNames(s.SearchInputContainer, {
                                [s['SearchInputContainer--Active']]:
                                    searchTerm.length > 0,
                            })}>
                            <Icon
                                name="Search"
                                className={s.SearchIcon}
                                size="Small"
                            />
                            <input
                                disabled={disabled || isEmptyAndUnfiltered}
                                type="text"
                                placeholder="Search person profiles"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className={s.SearchInput}
                            />
                        </div>
                    </div>
                </div>
                <div className={s.ListWrapper}>
                    {isClearingCompany ? (
                        <div className={s.Loading}>
                            <div className={s.LoadingIcon}>
                                <AnimatedLogo
                                    width={29}
                                    height={34}
                                    stroke="#616161"
                                />
                            </div>
                            <div className={s.LoadingText}>
                                {t(
                                    'connectionsPage.holdings.loadingIndividuals'
                                )}
                            </div>
                        </div>
                    ) : individuals.length === 0 ? (
                        <div className={s.ZeroStateWrapper}>
                            <IconText
                                title={t(
                                    'connectionsPage.holdings.noIndividualsLabel'
                                )}
                                text={
                                    selectedCompany
                                        ? t(
                                              'connectionsPage.holdings.noIndividualsText'
                                          )
                                        : t(
                                              'connectionsPage.holdings.noIndividualsTextAll'
                                          )
                                }
                                icon={'account_circle'}
                            />
                        </div>
                    ) : filteredIndividuals.length === 0 && isFiltering ? (
                        <div className={s.NoResults}>
                            <p>No results found</p>
                        </div>
                    ) : (
                        <div className={s.ListScrollWrapper}>
                            <div className={s.List}>
                                {filteredIndividuals
                                    .slice()
                                    .sort((a, b) =>
                                        a.name.localeCompare(b.name)
                                    )
                                    .map((individual) => (
                                        <ProfileCardLink
                                            {...individual}
                                            key={
                                                individual.primaryKey_ ||
                                                individual.url ||
                                                individual.name
                                            }
                                        />
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

ProfileCardFilterList.propTypes = {
    individuals: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            title: PropTypes.string,
            keyRole: PropTypes.string,
            company: PropTypes.string,
            politicalAffiliation: PropTypes.arrayOf(PropTypes.string),
            url: PropTypes.string,
        })
    ),
};

export default ProfileCardFilterList;
